import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDesignStore } from '../../../store/designStore';
import { useFeatureFlag } from '../../../store/configStore';
import styles from './DevDesignOverlay.module.scss';
import { IconSettings } from '@tabler/icons-react';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';

import NavbarTab from './NavbarTab';
import FooterTab from './FooterTab';
import KeyVisualsTab from './KeyVisualsTab';
import CampaignFlowTab from './CampaignFlowTab';
import HeroSectionTab from './HeroSectionTab';
import CountdownTab from './CountdownTab';
import CampaignMetricsTab from './CampaignMetricsTab';
import ProjectPageTab from './ProjectPageTab';
import ExportConfigTab from './ExportConfigTab';
import DbSyncSection from './DbSyncSection';

const PanelContent = ({
    config,
    updateLocalHeroConfig,
    updateLocalHeroTitleConfig,
    updateLocalHeroLeadGraphicConfig,
    updateLocalHeroCTAConfig,
    updateLocalKeyVisualConfig,
    updateLocalMetricsConfig,
    updateLocalCampaignFlowConfig,
    updateLocalCountdownConfig,
    updateLocalNavMenuConfig,
    updateLocalFooterConfig,
    updateLocalProjectPageConfig,
}) => {
    const [activeTab, setActiveTab] = useState('navbar');
    const { adminToken } = useDesignStore();

    const isProd = process.env.NODE_ENV === 'production';
    const isGated = isProd && !adminToken;

    if (isGated) {
        return (
            <div className="container-fluid py-4 ps-3 pe-4 bg-light min-vh-100 d-flex flex-column justify-content-center">
                <div className="card mb-3 shadow-sm border-danger">
                    <div className="card-body py-4 text-center">
                        <h1 className="h5 mb-2 fw-bold text-dark">Design-Customizer</h1>
                        <p className="text-muted small mb-0">
                            Bitte melden Sie sich mit Ihren Administrator-Zugangsdaten an, um den Customizer in der
                            Produktionsumgebung zu nutzen.
                        </p>
                    </div>
                </div>
                <DbSyncSection />
            </div>
        );
    }

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    const {
        countdown = {},
        navMenu = {},
        footer = {},
        hero = {},
        keyVisual = {},
        metrics = {},
        campaignFlow = {},
        projectPage = {},
    } = config;

    return (
        <div className="container-fluid py-3 ps-3 pe-4 bg-light min-vh-100">
            <div className="card mb-3 shadow-sm">
                <div className="card-body py-3">
                    <h1 className="h5 mb-1 fw-bold text-dark">Design-Customizer</h1>
                    <p className="text-muted small mb-0">Live-Entwicklungstool zur Design-Anpassung</p>
                </div>
            </div>

            <Nav tabs className="mb-3 bg-light sticky-top pt-2" style={{ top: 0, zIndex: 1020 }}>
                <NavItem>
                    <NavLink tag="button" active={activeTab === 'navbar'} onClick={() => toggleTab('navbar')}>
                        Navigationsleiste
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag="button" active={activeTab === 'footer'} onClick={() => toggleTab('footer')}>
                        Fußzeile (Footer)
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag="button" active={activeTab === '2'} onClick={() => toggleTab('2')}>
                        Key-Visuals
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag="button" active={activeTab === '3'} onClick={() => toggleTab('3')}>
                        Kampagnenablauf
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag="button" active={activeTab === '4'} onClick={() => toggleTab('4')}>
                        Hero-Bereich
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag="button" active={activeTab === '5'} onClick={() => toggleTab('5')}>
                        Countdown
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag="button" active={activeTab === '6'} onClick={() => toggleTab('6')}>
                        Metriken
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag="button" active={activeTab === '7'} onClick={() => toggleTab('7')}>
                        Projektseite
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink tag="button" active={activeTab === '8'} onClick={() => toggleTab('8')}>
                        Konfiguration exportieren
                    </NavLink>
                </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
                <TabPane tabId="navbar">
                    <NavbarTab navMenu={navMenu} updateLocalNavMenuConfig={updateLocalNavMenuConfig} />
                </TabPane>

                <TabPane tabId="footer">
                    <FooterTab footer={footer} updateLocalFooterConfig={updateLocalFooterConfig} />
                </TabPane>

                <TabPane tabId="2">
                    <KeyVisualsTab keyVisual={keyVisual} updateLocalKeyVisualConfig={updateLocalKeyVisualConfig} />
                </TabPane>

                <TabPane tabId="3">
                    <CampaignFlowTab
                        campaignFlow={campaignFlow}
                        updateLocalCampaignFlowConfig={updateLocalCampaignFlowConfig}
                    />
                </TabPane>

                <TabPane tabId="4">
                    <HeroSectionTab
                        hero={hero}
                        updateLocalHeroConfig={updateLocalHeroConfig}
                        updateLocalHeroTitleConfig={updateLocalHeroTitleConfig}
                        updateLocalHeroLeadGraphicConfig={updateLocalHeroLeadGraphicConfig}
                        updateLocalHeroCTAConfig={updateLocalHeroCTAConfig}
                    />
                </TabPane>

                <TabPane tabId="5">
                    <CountdownTab countdown={countdown} updateLocalCountdownConfig={updateLocalCountdownConfig} />
                </TabPane>

                <TabPane tabId="6">
                    <CampaignMetricsTab metrics={metrics} updateLocalMetricsConfig={updateLocalMetricsConfig} />
                </TabPane>

                <TabPane tabId="7">
                    <ProjectPageTab
                        projectPage={projectPage}
                        updateLocalProjectPageConfig={updateLocalProjectPageConfig}
                    />
                </TabPane>

                <TabPane tabId="8">
                    <ExportConfigTab config={config} />
                </TabPane>
            </TabContent>

            <div className="border-top pt-3 text-muted small">
                <span>* Änderungen werden sofort im Speicher angewendet.</span>
            </div>
        </div>
    );
};

const DevDesignOverlay = () => {
    const [popupWindow, setPopupWindow] = useState(null);
    const {
        config,
        updateLocalHeroConfig,
        updateLocalHeroTitleConfig,
        updateLocalHeroLeadGraphicConfig,
        updateLocalHeroCTAConfig,
        updateLocalKeyVisualConfig,
        updateLocalMetricsConfig,
        updateLocalCampaignFlowConfig,
        updateLocalCountdownConfig,
        updateLocalNavMenuConfig,
        updateLocalFooterConfig,
        updateLocalProjectPageConfig,
    } = useDesignStore();

    const [visibleInConsole, setVisibleInConsole] = useState(false);
    const popupWindowRef = useRef(popupWindow);

    useEffect(() => {
        popupWindowRef.current = popupWindow;
    }, [popupWindow]);

    // Close the popup window automatically on component unmount
    useEffect(() => {
        return () => {
            if (popupWindow) {
                popupWindow.close();
            }
        };
    }, [popupWindow]);

    useEffect(() => {
        window.openDevOverlay = () => {
            setVisibleInConsole(true);
            if (process.env.NODE_ENV !== 'production') {
                console.log(
                    '%c[Design-Customizer]%c Overlay wurde aktiviert. Sie sehen nun den Einstellungs-Button unten rechts.',
                    'color: #007bff; font-weight: bold;',
                    'color: inherit;'
                );
            }
        };
        window.closeDevOverlay = () => {
            setVisibleInConsole(false);
            if (popupWindowRef.current) {
                popupWindowRef.current.close();
            }
            if (process.env.NODE_ENV !== 'production') {
                console.log(
                    '%c[Design-Customizer]%c Overlay wurde deaktiviert und ausgeblendet.',
                    'color: #dc3545; font-weight: bold;',
                    'color: inherit;'
                );
            }
        };
        return () => {
            delete window.openDevOverlay;
            delete window.closeDevOverlay;
        };
    }, []);

    const isProd = process.env.NODE_ENV === 'production';
    const devOverlayEnabled = useFeatureFlag('devOverlayEnabled');

    if (isProd && !devOverlayEnabled) {
        return null;
    }

    const shouldShowTrigger = !isProd || visibleInConsole;
    if (!shouldShowTrigger || !config) {
        return null;
    }

    const openPopup = () => {
        if (popupWindow) {
            popupWindow.focus();
            return;
        }

        const w = window.open(
            '',
            'DevDesignCustomizer',
            'width=520,height=800,left=100,top=100,resizable=yes,scrollbars=yes,status=no,location=no,toolbar=no,menubar=no'
        );

        if (w) {
            const doc = w.document;
            doc.title = 'Design Customizer';

            // Copy Webpack styles to the popup
            const stylesList = window.document.querySelectorAll('style, link[rel="stylesheet"]');
            stylesList.forEach((style) => {
                doc.head.appendChild(style.cloneNode(true));
            });

            // Add standard custom layout overrides for the popup body
            const styleOverride = doc.createElement('style');
            styleOverride.innerHTML = `
                body {
                    margin: 0;
                    font-family: var(--bs-font-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
                    overflow-x: hidden;
                    padding-bottom: 20px;
                    padding-right: 8px; /* Extra gutter spacing to prevent scrollbar overlap */
                }
            `;
            doc.head.appendChild(styleOverride);

            // Mount portal element
            const mountNode = doc.createElement('div');
            mountNode.id = 'dev-customizer-root';
            doc.body.appendChild(mountNode);

            setPopupWindow(w);

            w.addEventListener('beforeunload', () => {
                setPopupWindow(null);
            });
        }
    };

    const isWindowOpen = !!popupWindow;

    return (
        <div className={styles.wrapper}>
            {/* Floating Trigger Button to Popout Window */}
            <button className={styles.triggerBtn} onClick={openPopup} title="Design-Customizer im Popup öffnen">
                <IconSettings size={24} />
                <span className={styles.badge}>{isWindowOpen ? 'Offen' : 'Live'}</span>
            </button>

            {/* Portal Component to Popup Window Document */}
            {isWindowOpen &&
                createPortal(
                    <PanelContent
                        config={config}
                        updateLocalHeroConfig={updateLocalHeroConfig}
                        updateLocalHeroTitleConfig={updateLocalHeroTitleConfig}
                        updateLocalHeroLeadGraphicConfig={updateLocalHeroLeadGraphicConfig}
                        updateLocalHeroCTAConfig={updateLocalHeroCTAConfig}
                        updateLocalKeyVisualConfig={updateLocalKeyVisualConfig}
                        updateLocalMetricsConfig={updateLocalMetricsConfig}
                        updateLocalCampaignFlowConfig={updateLocalCampaignFlowConfig}
                        updateLocalCountdownConfig={updateLocalCountdownConfig}
                        updateLocalNavMenuConfig={updateLocalNavMenuConfig}
                        updateLocalFooterConfig={updateLocalFooterConfig}
                        updateLocalProjectPageConfig={updateLocalProjectPageConfig}
                    />,
                    popupWindow.document.getElementById('dev-customizer-root')
                )}
        </div>
    );
};

export default DevDesignOverlay;
